import { InfoTooltip } from "@/components/InfoTooltip";
import { attentionTip } from "@/features/operations/operationsLabels";
import type { AttentionSummary } from "../attentionNote";

interface Props {
  summary: AttentionSummary;
  /** Scrolls the Tasks table to the deciding task; absent when the cause is the work order or the task is not in the table. */
  onShowTask?: () => void;
  /** Hands "why is this request stuck / blocked?" to the Ask Claude panel; absent when the panel is not on this screen. */
  onAskClaude?: () => void;
}

const days = (n: number) => `${n} business ${n === 1 ? "day" : "days"}`;

/**
 * The Operations verdict on this request, under the header: Stuck, Blocked or both, with the two
 * clocks the rule read (Rafael, 2026-10-21). Rendered only for a flagged open request (the caller
 * passes nothing for Clear, closed, or unknown items).
 */
export function AttentionBanner({ summary, onShowTask, onAskClaude }: Props) {
  const s = summary;
  const holder = s.task ? (s.task.holder ? `${s.task.holder} (${s.task.roleLabel})` : s.task.roleLabel) : null;
  return (
    <section className={`att-banner ${s.state}`} aria-label={`${s.label}: untouched for ${s.untouchedBd} business days`} data-testid="attention-banner">
      <div>
        <div className="att-banner-title">
          <span className={`ops-pill att-${s.state}`}>
            <i className="dot" />
            {s.label}
          </span>
          <span className="since">
            untouched for {days(s.untouchedBd)}
            {s.sinceStateBd != null ? ` · state unchanged for ${days(s.sinceStateBd)}` : ""}
            {s.cause === "workOrder" ? " · waiting on the work order" : ""}
          </span>
          <InfoTooltip inline text={attentionTip(s.state, s.stuckAfterBd, s.blockedAfterBd)} />
        </div>
        <p className="att-banner-why">
          {s.isStuck && (
            <>
              <b>Stuck</b>: nobody touched this request since {s.lastTouched}, <b>{days(s.untouchedBd)}</b> ago, over the {s.stuckAfterBd}-day limit.{" "}
            </>
          )}
          {s.isBlocked && s.cause === "task" && s.task && (
            <>
              <b>Blocked</b>: the first open task, <b>{s.task.name}</b>, has sat with <b>{holder}</b> since {s.since}, <b>{days(s.sinceStateBd ?? 0)}</b> without moving to another actor or status, over the {s.blockedAfterBd}-day limit.{" "}
            </>
          )}
          {s.isBlocked && s.cause === "workOrder" && (
            <>
              <b>Blocked</b>: no task is pending and the work order has waited <b>{days(s.sinceStateBd ?? 0)}</b> {s.workOrderStep ?? ""}, over the {s.blockedAfterBd}-day limit, counted from {s.since} ({s.sinceAnchor}).{" "}
            </>
          )}
          {!s.isBlocked && s.cause === "task" && s.task && (
            <>The first open task, <b>{s.task.name}</b>, is with <b>{holder}</b> since {s.since}; that is within the {s.blockedAfterBd}-day limit, so it is not Blocked.{" "}</>
          )}
          {s.task && s.task.openCount > 1
            ? ` Tasks run in sequence, so the ${s.task.openCount - 1} ${s.task.openCount - 1 === 1 ? "task" : "tasks"} behind it ${s.task.openCount - 1 === 1 ? "is" : "are"} queued, not waiting.`
            : ""}
        </p>
        <dl className="att-banner-facts">
          <div><dt>Pending with</dt><dd>{s.pendingWith}</dd></div>
          {s.task ? (
            <div><dt>Task</dt><dd>{s.task.name} <span className="muted">({s.task.openIndex} of {s.task.openCount} open)</span></dd></div>
          ) : s.cause === "workOrder" ? (
            <div><dt>Work order</dt><dd>{s.workOrderStep === "to approve" ? "submitted, not approved" : "not submitted"}</dd></div>
          ) : null}
          <div><dt>Last touched</dt><dd>{s.lastTouched} · {s.untouchedBd} bd</dd></div>
          <div><dt>State since</dt><dd>{s.since ? `${s.since}${s.sinceAnchor ? ` (${s.sinceAnchor})` : ""} · ${s.sinceStateBd ?? 0} bd` : "no state clock"}</dd></div>
          <div><dt>Limits</dt><dd>touch {s.stuckAfterBd} bd · move {s.blockedAfterBd} bd</dd></div>
          <div><dt>Yesterday 08:00</dt><dd>{s.yesterday}</dd></div>
        </dl>
        <div className="att-banner-queue">{s.queueLine}</div>
      </div>
      <div className="att-banner-actions">
        {onShowTask && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onShowTask}>
            <span className="material-symbols-rounded">list_alt</span>
            Show task
          </button>
        )}
        {onAskClaude && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onAskClaude}>
            <span className="material-symbols-rounded">forum</span>
            Ask Claude why
          </button>
        )}
      </div>
    </section>
  );
}
